/* @refresh reset */
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { useLocation } from 'react-router-dom'
import { AnySidebarSection, SidebarNavigationItem, SidebarContextState } from '../../types/navigation'
import { getSidebarSectionFromUrl } from '../../utils/sidebarNavigation'

// Create the sidebar context
const SidebarContext = createContext<SidebarContextState | null>(null)

/**
 * Hook to use the sidebar context
 * @throws Error if used outside of SidebarProvider
 */
export const useSidebar = (): SidebarContextState => {
  const context = useContext(SidebarContext)
  if (!context) {
    throw new Error('useSidebar must be used within a SidebarProvider')
  }
  return context
}

/**
 * Props for the SidebarProvider component
 */
interface SidebarProviderProps {
  children: ReactNode
  navigationItems: SidebarNavigationItem[]
  defaultActiveSection?: AnySidebarSection
  defaultCollapsed?: boolean
}

/**
 * SidebarProvider component that provides sidebar state management
 * Uses React Context for local state management
 * Automatically syncs activeSection with current URL
 */
export const SidebarProvider: React.FC<SidebarProviderProps> = ({
  children,
  navigationItems,
  defaultActiveSection,
  defaultCollapsed = false
}) => {
  const location = useLocation()
  const [activeSection, setActiveSection] = useState<AnySidebarSection>(defaultActiveSection || navigationItems[0]?.id)
  const [isCollapsed, setIsCollapsed] = useState<boolean>(defaultCollapsed)

  // Sync sidebar active section with URL changes
  useEffect(() => {
    const sectionFromUrl = getSidebarSectionFromUrl(location.pathname)

    if (sectionFromUrl) {
      // Check if section exists in navigation items
      const navItem = navigationItems.find((item) => item.id === sectionFromUrl)

      // Only update if it's a visible sidebar item (not hidden)
      if (navItem && !navItem.hideInSidebar) {
        setActiveSection(sectionFromUrl)
      }
      // For hidden items or non-sidebar routes, keep current highlight
      // This prevents the sidebar from losing highlight when navigating to
      // routes like /gamer/game-center (which is hideInSidebar: true)
      // Note: achievements route is mapped to MY_PROFILE in getSidebarSectionFromUrl
    }
  }, [location.pathname, navigationItems])

  const contextValue: SidebarContextState = {
    activeSection,
    setActiveSection,
    navigationItems,
    isCollapsed,
    setIsCollapsed
  }

  return <SidebarContext.Provider value={contextValue}>{children}</SidebarContext.Provider>
}
